import React, { useEffect, useRef, useState } from 'react';
import { t as uiText } from '../../../i18n';
import { useEmailVerification } from '../EmailVerificationContext';
import { formatRemaining } from '../phase';
import ResendButton from './ResendButton';
import ChangeEmailButton from './ChangeEmailButton';
import EmailHelp from './EmailHelp';

/** ヘッダーの「メール未認証」表示。ホバー/クリック/タップで説明を開く（スマホでも文字は常に表示） */
const EmailUnverifiedBadge: React.FC = () => {
  const { phase, remainingMs, showPublicRag } = useEmailVerification();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (phase !== 'A' && phase !== 'B' && phase !== 'C') return null;
  const { hours, minutes } = formatRemaining(remainingMs);

  return (
    <div ref={wrapRef} className="ev-badge-wrap" onMouseEnter={() => setOpen(true)}>
      <button
        type="button"
        className="ev-badge"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="ev-badge-popover"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
          <path d="M12 9v.01" />
        </svg>
        <span>{uiText('Email unverified')}</span>
      </button>
      {open && (
        <div id="ev-badge-popover" className="ev-popover" role="dialog" aria-label={uiText('Email unverified')}>
          <p>{uiText('Your email address has not been verified.')}</p>
          <p>
            {showPublicRag
              ? uiText(
                  'If email verification is not completed within 72 hours of creating your account, normal features will stop. The public RAG may also stop under some conditions.'
                )
              : uiText(
                  'If email verification is not completed within 72 hours of creating your account, normal features will stop.'
                )}
          </p>
          {showPublicRag && (
            <p>
              {uiText(
                'A public RAG already installed on your website may become unavailable, so please be careful.'
              )}
            </p>
          )}
          <p>{uiText('If the email address is wrong, you can change it.')}</p>
          {phase !== 'C' && (
            <p className="ev-banner-remaining">
              {uiText('Time remaining: {{hours}}h {{minutes}}m', { hours, minutes })}
            </p>
          )}
          <div className="ev-actions ev-actions-stack">
            <ResendButton />
            <ChangeEmailButton />
          </div>
          <EmailHelp />
        </div>
      )}
    </div>
  );
};

export default EmailUnverifiedBadge;
